import React from 'react';
import connectToStores from 'fluxible-addons-react/connectToStores';
import shallowCompare from '../../lib/shallowCompare';
import { formatCurrency } from '../../../client/lib/utils';
import WineSkuStore from '../../stores/WineSkuStore';
import RouteStore from '../../stores/RouteStore';
import InventoryRecordActions from '../../actions/InventoryRecordActions';
import Helmet from 'react-helmet';
import Breadcrumbs from '../../components/Breadcrumbs';
import DelectableRating from '../../components/DelectableRating';
import ProductAttributes from '../../components/ProductAttributes';
import ProductReviewsList from '../../components/ProductReviewsList';
import styles from './index.sass';

class WineSkuContainer extends React.Component {
  static contextTypes = {
    executeAction: React.PropTypes.func
  };

  shouldComponentUpdate(nextProps, nextState) {
    return shallowCompare(this, nextProps, nextState);
  }

  getHelmetProps(wineSku) {
    let description = wineSku.wine_profile.description;
    let title = wineSku.wine_profile.name;
    let helmetProps = {
      title: title,
      meta: [
        {name: 'description', content: description},
        {property: 'og:title', content: title},
        {property: 'og:description', content: description},
        {property: 'og:url', content: this.props.currentRoute.absolute_url},
        {name: 'twitter:card', content: 'summary_large_image'},
        {name: 'twitter:title', content: title},
        {name: 'twitter:description', content: description}
      ]
    };

    let photo = wineSku.wine_profile.photo_hash.getChildByWidth(640);
    let photoUrl;
    if(photo && photo.width && photo.height) {
      photoUrl = photo.url;
      helmetProps.meta.push({property: 'og:image:width', content: photo.width});
      helmetProps.meta.push({property: 'og:image:height', content: photo.height});
      helmetProps.meta.push({property: 'og:image', content: photoUrl});
      helmetProps.meta.push({name: 'twitter:image', content: photoUrl});
    }
    return helmetProps;
  }

  renderOffers(wineSku) {
    let offers = wineSku.offers;
    if(!(offers && offers.length)) {return <div className={styles.empty}>Currently unavailable</div>;}
    let headerRow = (
      <div className={`${styles.offer} ${styles.header}`} key="header">
        <div className={styles.store}>Store</div>
        <div className={styles.store}>Availability</div>
        <div className={styles.promotions}>Promotions</div>
        <div className={styles.actions}></div>
      </div>
    );
    let sectionTitle = <p className={styles.offerSectionTitle} key="sectionTitle">Available at {wineSku.offers.length} store{wineSku.offers.length > 1 ? 's' : ''}</p>;
    return [sectionTitle, headerRow].concat(offers.map((offer) => {
      return (
        <div className={styles.offer} key={offer.url}>
          <div className={styles.store}>
            <a className={styles.name} href={offer.store.url} target="_blank">{offer.store.name}</a>
            <p className={styles.location}>{offer.store.location}</p>
          </div>
          <div className={styles.store}>
           <p className={styles.location}>{offer.quantity > 0 ? 'In Stock' : 'Sold Out'}</p>
          </div>
          <div className={styles.promotions}>
            {(offer.store.promotions || []).map((promotion, i) => {
              return <p className={styles.promotion} key={i}>{promotion}</p>;
            })}
          </div>
          <div className={styles.actions}>
            <a className={styles.shopLink} href={offer.url}>Shop</a>
          </div>
        </div>
      );
    }));
  }

  render() {
    let { prevRoute, wineSku } = this.props;
    if(!wineSku) {return null;}
    let wineProfile = wineSku.wine_profile;
    let breadcrumbsProps = {
      links: [{routeName: 'home'}, {routeName: 'universalSearch', text: 'Search'}]
    };

    if(prevRoute && prevRoute.name === 'universalSearch') {
      let { filters } = prevRoute.query;
      if(filters && filters.length) {
        filters = filters.map((f) => {
          switch(f.type) {
            case 'min_price_in_cents':
              return `Over ${formatCurrency(f.value, {withoutTrailingZeros: true})}`;
            case 'max_price_in_cents':
              return `Under ${formatCurrency(f.value, {withoutTrailingZeros: true})}`;
            default:
              return f.value;
          }
        });
        breadcrumbsProps.links.push({text: `“${filters.join(' ')}”`, href: prevRoute.url});
      }
    }

    let delectableRating;
    if(wineProfile.rating) {
      let delectableRatingProps = {
        rating: wineProfile.rating.average_rating,
        count: wineProfile.rating.ratings_count,
        style: {whiteSpace: 'pre'}
      };
      delectableRating = <DelectableRating {...delectableRatingProps} />;
    }

    let searchFilters = [{type: 'volume', value: wineSku.volume_text}];
    wineProfile.attributes.push({
      type   : 'text_link',
      object : {
        header   : 'Volume',
        text     : wineSku.volume_text,
        deeplink : null,
        url      : null
      }
    });

    return (
      <section className={styles.container}>
        <Helmet {...this.getHelmetProps(wineSku)} />
        <Breadcrumbs {...breadcrumbsProps} />
        <div className={styles.wineSku}>
          <div className={styles.wineProfile}>
            <img className={styles.photo} src={wineProfile.photo_hash.getClosestToTarget(640)} />
            <div className={styles.details}>
              <p className={styles.name}>{`${wineProfile.producer} ${wineProfile.name}`}</p>
              <div className={styles.ratingAndPriceRow}>
                {delectableRating}
                {delectableRating ? <span className={styles.bullet}>•</span> : null}
                <span className={styles.priceRange}>{wineSku.price_range_text}</span>
              </div>
              <ProductAttributes attributes={wineProfile.attributes} />
            </div>
          </div>
          <div className={styles.separator} />
          <div className={styles.offers}>
            {this.renderOffers(wineSku)}
          </div>
          <div className={styles.separator} />
          <ProductReviewsList reviews={wineProfile.reviews} />
        </div>
      </section>
    );
  }
}

WineSkuContainer = connectToStores(WineSkuContainer, [WineSkuStore], (context, props) => {
  let { id } = props.currentRoute.params;
  let prevRoute = context.getStore(RouteStore).getPreviousRoute();
  return {
    wineSku: context.getStore(WineSkuStore).getEntityById(id),
    prevRoute: prevRoute
  };
});

export default WineSkuContainer;



// WEBPACK FOOTER //
// banquet/containers/WineSkuContainer/index.js